import React, { JSX, useEffect, useState } from "react";
import { useWallet } from "../../../hooks/useWallet";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PaymentType, Transaction, TransactionStatus } from "../../../models/Transactions";
import Loader from "../components/Loader";
import { dateStringToLocaleDate, dateStringToLocaleHour, formatAmount, formatAmountWithSATS } from "../../../utils/Utils";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { useTheme } from "../../../hooks/useTheme";
import ButtonsInfosTxSenderReceiver from "./components/ButtonsInfosTxSenderReceiver";
import { BulkTypes } from "../../../models/BulkPayment";

interface TransactionDetailsProps {
  
}

const TransactionDetails: React.FC<TransactionDetailsProps> = () => {
    const { refreshTransaction, getTransaction, markTransactionAsRead, refresh } = useWallet();
    const { paymentHash } = useParams();
    const [transaction, setTransaction] = useState<Transaction | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const {isMobile, isDarkMode} = useTheme();

    useEffect(() => {
        if (paymentHash) {
            getTransaction(paymentHash)
                .then((transaction) => {
                    if(transaction) setTransaction(transaction);
                })
                .finally(() => setIsLoading(false));
        }
    }, [paymentHash, getTransaction]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined;

        if (transaction) {
            if(transaction.read === false)
                markTransactionAsRead(transaction.paymentHash);

            if (transaction.type === PaymentType.bulk && transaction.status === TransactionStatus.PENDING) {
                intervalId = setInterval(() => {
                    refreshTransaction(transaction.paymentHash)
                        .then((transaction) => {
                            if (transaction) {
                                setTransaction(transaction);

                                if (transaction.status === TransactionStatus.SUCCESS && intervalId) {
                                    clearInterval(intervalId);
                                    intervalId = undefined;
                                }
                            }
                        })
                        .finally(() => setIsLoading(false));
                    refresh();
                }, 5000);
            }
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [transaction, markTransactionAsRead, refreshTransaction]);

    const renderTextWithLinks = (text: string): JSX.Element[] => {
        // Improved regex to detect both full URLs and plain domain names
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)*\.(com|net|info|app|io|org)\b)/g;
        
        // Create an array to hold our result elements
        const result: JSX.Element[] = [];
        
        // Find all matches
        const matches = Array.from(text.matchAll(urlRegex));
        let lastIndex = 0;
        
        // Process each match
        matches.forEach((match, i) => {
            const matchIndex = match.index!;
            const matchText = match[0];
            
            // Add text before the match
            if (matchIndex > lastIndex) {
                result.push(
                    <span key={`text-${i}`}>
                        {text.substring(lastIndex, matchIndex)}
                    </span>
                );
            }
            
            // Add the link
            const href = matchText.startsWith('http') ? matchText : `https://${matchText}`;
            result.push(
                <a 
                    key={`link-${i}`} 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline text-brand-orange break-all"
                >
                    {matchText}
                </a>
            );
            
            lastIndex = matchIndex + matchText.length;
        });
        
        // Add any remaining text after the last match
        if (lastIndex < text.length) {
            result.push(
                <span key="text-last">
                    {text.substring(lastIndex)}
                </span>
            );
        }
        
        return result;
    };

    const renderInside = () => {
        if(isLoading) return <Loader />;

        if(transaction)
        {
            const isSend = transaction.amount <= 0;
            
            const showTemplateSuccess = location.state?.template === 'success' && transaction.type !== PaymentType.bulk;

            if(showTemplateSuccess) {
                return (
                    <>
                        <div className="flex flex-col justify-center items-center flex-1 gap-8 p-4">
                            <div className="flex flex-col items-center">
                                <img src={"/images/payment_success.svg"} alt="" className="w-60 h-60" />
                            </div>

                            <div className="flex flex-col items-center">
                                <div className={`font-semibold text-3xl`}>
                                    Zap Sent
                                </div>
                            </div>
        
                            <div className="flex flex-col items-center">
                                <div className={`font-semibold text-3xl`}>
                                    {formatAmountWithSATS(Math.abs(transaction?.amount))}
                                </div>
                            </div>
                            
                            <ButtonsInfosTxSenderReceiver transaction={transaction} />
                        </div>
        
                        <div className="flex flex-col items-center p-4 mb-2">
                        {
                            isDarkMode ?
                                <img src={"/images/opa_logo_text.png"} alt="" className="w-[190px]" />
                            :
                                <img src={"/images/opa_logo_text_light.png"} alt="" className="w-[190px]" />
                        }
                        </div>
                    </>
                );
            }
            else {
                let titlePayment = '';

                if(isSend) {
                    titlePayment = 'Zap Sent';

                    if(transaction.bulk) {
                        if(transaction.bulk?.type === BulkTypes.city || transaction.bulk?.type === BulkTypes.distanceFromMyself || transaction.bulk?.type === BulkTypes.special)
                            titlePayment = 'Geo Zap Sent';
                        else
                            titlePayment = 'Mass Zap Sent';
                    }
                }
                else if(transaction.fromBulk) {
                    if(transaction.fromBulk?.type === BulkTypes.city || transaction.fromBulk?.type === BulkTypes.distanceFromMyself || transaction.bulk?.type === BulkTypes.special)
                        titlePayment = 'Geo Zap Received';
                    else
                        titlePayment = 'Mass Zap Received';
                }
                else {
                    titlePayment = 'Zap Received';
                }

                
                return (
                    <div className="flex flex-col justify-center items-center flex-1 gap-8 p-4 max-w-[100vw]">
                        {
                            transaction.status === TransactionStatus.PENDING ? (
                                <>
                                    <div className="flex flex-col items-center">
                                        <Loader />
                                    </div>

                                    <div className="text-center text-4xl">
                                        Sending...
                                    </div>

                                    <div className="bg-brand-bg-5 px-4 py-3 rounded-lg flex items-center gap-2">
                                        <span className="material-icons">info</span>
                                        <span>Zap in progress. You may leave this page.</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex flex-col items-center">
                                        <img src={"/images/payment_success.png"} alt="" className="w-40 h-40 rounded-full" />
                                    </div>

                                    <div className="text-center text-4xl">
                                        {titlePayment}
                                    </div>
                                </>
                            )
                        }
                    
                        <ButtonsInfosTxSenderReceiver transaction={transaction} />

                        <div className="flex flex-col items-center">
                            <div className={`font-semibold text-3xl ${isSend ? '' : 'text-brand-green'}`}>
                                {transaction.amount > 0 ? '+' : '-'}{formatAmount(Math.abs(transaction.amount))} SATS
                            </div>
                            {
                                isSend && (
                                    <div className="flex flex-col items-center mt-2">
                                        {
                                            transaction?.opaFee && transaction?.routingFee ? (
                                                <>
                                                    <div className="text-brand-color-2 text-sm">Orange Pill App Fees : {formatAmount(Math.abs(transaction.opaFee))} SATS</div>
                                                    <div className="text-brand-color-2 text-sm">Routing Fees : {formatAmount(Math.abs(transaction.routingFee))} SATS</div>
                                                </>
                                            ) : (
                                                Math.abs(transaction.fee) !== 0 && (
                                                    <div className="text-brand-color-2 text-sm">Fee : {formatAmount(Math.abs(transaction.fee))} SATS</div>
                                                )
                                            )
                                        }
                                    </div>
                                )
                            }
                            <div className="flex flex-col items-center text-brand-color-2 text-sm mt-2">
                                <div>{dateStringToLocaleDate(transaction.time)}</div>
                                <div>{dateStringToLocaleHour(transaction.time)}</div>
                            </div>
                        </div>

                        {
                            transaction.status === TransactionStatus.PENDING && transaction.type === PaymentType.bulk && (
                                <div className="flex flex-col items-center hidden">
                                    <div className="font-semibold text-xl text-center mb-2">{transaction.bulk?.type === BulkTypes.city || transaction.bulk?.type === BulkTypes.distanceFromMyself || transaction.bulk?.type === BulkTypes.special ? 'Geo Zapping' : 'Mass Zap'}:</div>
                                    <div className="text-center text-brand-color-2">{transaction.bulk?.nbSendedSuccess && formatAmount(transaction.bulk?.nbSendedSuccess)} / {transaction.bulk?.nbToSend && formatAmount(transaction.bulk?.nbToSend)}</div>
                                    {(transaction.bulk?.nbSendedError ?? 0) > 0 && <div className="text-center text-brand-color-2">{transaction.bulk?.nbSendedError && formatAmount(transaction.bulk?.nbSendedError)} errors</div>}
                                </div>
                            )
                        }
                        

                        {
                            transaction.comment && (
                                <div className="">
                                    <div className="font-semibold text-xl text-center mb-2">Message:</div>
                                    <div className="max-w-[500px] text-center text-brand-color-2 break-words">{renderTextWithLinks(transaction.comment)}</div>
                                </div>
                            )
                        }
                    </div>
                );
            }
        }
        else {
            return null;
        }
    }

    const onClickPrevArrow = () => {
        if(isMobile)
            return () => navigate(`/home/wallet`);

        return undefined;
    }        

    return <LayoutWithTopBar
        breadcrumbs={[
            { title: 'Wallet', onClick: () => navigate('/home/wallet') },
            { title: 'Transaction' }
        ]}
        onClickPrevArrow={onClickPrevArrow()}
    >
        {renderInside()}
    </LayoutWithTopBar>
}

export default TransactionDetails;